import type { ReactNode } from 'react';

export const CANVAS_SEARCH_PARAM = 'canvas';

export type CanvasO11yResource = {
  type: string;
  [key: string]: unknown;
};

export type CanvasAppRouteView = {
  kind: 'app-route';
  pattern: string;
  params: Record<string, string>;
  environmentKey?: string;
  query?: string;
};

export type CanvasView =
  | { kind: 'empty' }
  | { kind: 'o11y-resource'; resource: CanvasO11yResource }
  | CanvasAppRouteView;

export type CanvasLastWrite = {
  resource: 'flag';
  projectKey: string;
  flagKey: string;
  environmentKey: string;
  outcome: 'applied' | 'approval_requested';
  flagVersion?: number;
  approvalRequestId?: string;
  at: string;
  turn: number;
};

export type CanvasAppRouteRenderer = (props: {
  view: CanvasAppRouteView;
  onOpenView: (view: CanvasView) => void;
  lastWrite?: CanvasLastWrite;
}) => ReactNode;

export type CanvasState = {
  revision: number;
  activeRevision?: number;
  updatedBy: 'user' | 'agent';
  active: CanvasView;
  lastWrite?: CanvasLastWrite;
};

export type CanvasSession = {
  view: CanvasView;
  humanLock: boolean;
  lastSeenServerRevision: number;
  lastSeenActiveRevision: number;
  lastWrite?: CanvasLastWrite;
};

export const EMPTY_CANVAS_VIEW: CanvasView = { kind: 'empty' };

const EMPTY_SESSION: CanvasSession = {
  view: EMPTY_CANVAS_VIEW,
  humanLock: false,
  lastSeenServerRevision: -1,
  lastSeenActiveRevision: -1,
};

export function canvasViewKey(view: CanvasView): string {
  return JSON.stringify(view);
}

export function parseCanvasDocument(value: unknown): CanvasState | null {
  const parsed = typeof value === 'string' ? parseJson(value) : value;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const revision = record.revision;
  const updatedBy = record.updatedBy ?? record.updated_by;
  if (typeof revision !== 'number' || !Number.isFinite(revision)) {
    return null;
  }
  if (updatedBy !== 'user' && updatedBy !== 'agent') {
    return null;
  }
  const active = parseCanvasView(record.active);
  if (!active) {
    return null;
  }
  const activeRevision = record.activeRevision ?? record.active_revision;
  const lastWrite = parseLastWrite(record.lastWrite ?? record.last_write);
  return {
    revision,
    ...(typeof activeRevision === 'number' && Number.isFinite(activeRevision) ? { activeRevision } : {}),
    updatedBy,
    active,
    ...(lastWrite ? { lastWrite } : {}),
  };
}

export function lastWriteKey(lastWrite: CanvasLastWrite | undefined): string | null {
  if (!lastWrite) {
    return null;
  }
  return [
    lastWrite.projectKey,
    lastWrite.flagKey,
    lastWrite.environmentKey,
    lastWrite.outcome,
    lastWrite.flagVersion ?? '',
    lastWrite.approvalRequestId ?? '',
    lastWrite.at,
  ].join('|');
}

export function encodeCanvasParam(state: CanvasState): string {
  const bytes = new TextEncoder().encode(JSON.stringify(state));
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

export function decodeCanvasParam(param: string | null | undefined): CanvasState | null {
  if (!param) {
    return null;
  }
  try {
    const binary = atob(param);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return parseCanvasDocument(JSON.parse(new TextDecoder().decode(bytes)));
  } catch {
    return null;
  }
}

export function initialCanvasSession(urlCanvas: CanvasState | null): CanvasSession {
  if (!urlCanvas) {
    return EMPTY_SESSION;
  }
  return {
    view: urlCanvas.active,
    humanLock: urlCanvas.updatedBy === 'user',
    lastSeenServerRevision: urlCanvas.revision,
    lastSeenActiveRevision: urlCanvas.activeRevision ?? urlCanvas.revision,
    ...(urlCanvas.lastWrite ? { lastWrite: urlCanvas.lastWrite } : {}),
  };
}

export function applyServerCanvas(session: CanvasSession, server: CanvasState): CanvasSession {
  if (server.revision <= session.lastSeenServerRevision) {
    return session;
  }
  const lastWrite = server.lastWrite ?? session.lastWrite;
  const written: CanvasSession = {
    ...session,
    lastSeenServerRevision: server.revision,
    ...(lastWrite ? { lastWrite } : {}),
  };
  const activeRevision = server.activeRevision ?? server.revision;
  if (activeRevision <= session.lastSeenActiveRevision) {
    return written;
  }
  const seen: CanvasSession = { ...written, lastSeenActiveRevision: activeRevision };
  if (session.humanLock) {
    return seen;
  }
  if (canvasViewKey(session.view) === canvasViewKey(server.active)) {
    return seen;
  }
  return { ...seen, view: server.active };
}

export function applyUserView(session: CanvasSession, view: CanvasView): CanvasSession {
  return { ...session, view, humanLock: true };
}

export function releaseHumanLock(session: CanvasSession): CanvasSession {
  if (!session.humanLock) {
    return session;
  }
  return { ...session, humanLock: false };
}

export function canvasStateFromSession(session: CanvasSession): CanvasState {
  return {
    revision: session.lastSeenServerRevision,
    activeRevision: session.lastSeenActiveRevision,
    updatedBy: session.humanLock ? 'user' : 'agent',
    active: session.view,
    ...(session.lastWrite ? { lastWrite: session.lastWrite } : {}),
  };
}

export function canvasParamForSession(session: CanvasSession): string | null {
  if (session.view.kind === 'empty' && !session.humanLock) {
    return null;
  }
  return encodeCanvasParam(canvasStateFromSession(session));
}

export function classifyCanvasQueryError(error: unknown): 'missing' | 'subselection' | 'other' | 'none' {
  if (!error) {
    return 'none';
  }
  const message = errorText(error);
  if (/cannot query field/i.test(message) && /canvas/i.test(message)) {
    return 'missing';
  }
  if (/unknown field/i.test(message) && /canvas/i.test(message)) {
    return 'missing';
  }
  if (/must have a selection of subfields/i.test(message) || /must have sub selection/i.test(message)) {
    return 'subselection';
  }
  return 'other';
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function parseCanvasView(value: unknown): CanvasView | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (record.kind === 'empty') {
    return EMPTY_CANVAS_VIEW;
  }
  if (record.kind === 'o11y-resource') {
    const resource = parseO11yResource(record.resource);
    if (!resource) {
      return null;
    }
    return { kind: 'o11y-resource', resource };
  }
  if (record.kind === 'app-route') {
    if (typeof record.pattern !== 'string' || !isAllowlistedPatternShape(record.pattern)) {
      return null;
    }
    const params = parseStringRecord(record.params);
    if (!params) {
      return null;
    }
    if (record.search !== undefined && typeof record.search !== 'string') {
      return null;
    }
    if (record.environmentKey !== undefined && typeof record.environmentKey !== 'string') {
      return null;
    }
    if (record.query !== undefined && typeof record.query !== 'string') {
      return null;
    }
    const legacy = parseLegacySearch(typeof record.search === 'string' ? record.search : '');
    const environmentKey = nonEmpty(record.environmentKey) ?? legacy.environmentKey;
    const query = nonEmpty(record.query) ?? legacy.query;
    return {
      kind: 'app-route',
      pattern: record.pattern,
      params,
      ...(environmentKey ? { environmentKey } : {}),
      ...(query ? { query } : {}),
    };
  }
  return null;
}

function nonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function parseLegacySearch(search: string): { environmentKey?: string; query?: string } {
  if (!search) {
    return {};
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return {
    environmentKey: nonEmpty(params.get('selected-env')) ?? nonEmpty(params.get('env')),
    query: nonEmpty(params.get('q')),
  };
}

function parseLastWrite(value: unknown): CanvasLastWrite | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const { projectKey, flagKey, environmentKey, outcome, at, turn, flagVersion, approvalRequestId } = record;
  if (record.resource !== 'flag') {
    return undefined;
  }
  if (typeof projectKey !== 'string' || typeof flagKey !== 'string' || typeof environmentKey !== 'string') {
    return undefined;
  }
  if (outcome !== 'applied' && outcome !== 'approval_requested') {
    return undefined;
  }
  if (typeof at !== 'string' || typeof turn !== 'number') {
    return undefined;
  }
  return {
    resource: 'flag',
    projectKey,
    flagKey,
    environmentKey,
    outcome,
    at,
    turn,
    ...(typeof flagVersion === 'number' ? { flagVersion } : {}),
    ...(typeof approvalRequestId === 'string' ? { approvalRequestId } : {}),
  };
}

function parseO11yResource(value: unknown): CanvasO11yResource | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.type !== 'string' || record.type.length === 0) {
    return null;
  }
  const resource: CanvasO11yResource = { type: record.type };
  for (const [key, entry] of Object.entries(record)) {
    resource[key] = entry;
  }
  return resource;
}

function parseStringRecord(value: unknown): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const params: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry !== 'string') {
      return null;
    }
    params[key] = entry;
  }
  return params;
}

function isAllowlistedPatternShape(pattern: string): boolean {
  if (!pattern.startsWith('/')) {
    return false;
  }
  if (pattern.includes('://') || pattern.startsWith('//')) {
    return false;
  }
  return true;
}

function errorText(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (!error || typeof error !== 'object') {
    return '';
  }
  const record = error as { message?: unknown; graphQLErrors?: Array<{ message?: unknown }> };
  const parts: string[] = [];
  if (typeof record.message === 'string') {
    parts.push(record.message);
  }
  for (const gqlError of record.graphQLErrors ?? []) {
    if (typeof gqlError.message === 'string') {
      parts.push(gqlError.message);
    }
  }
  return parts.join('\n');
}
